/**
 * Limit Reached Page
 * Shown when a QR code scan limit has been reached
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Zap } from 'lucide-react';

export default function LimitReachedPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    // Optional: Redirect to home after countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[--clinqr-bg-subtle] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        {/* Warning Icon */}
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-8 h-8 text-amber-600" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-[--clinqr-text-primary] mb-2">Scan Limit Reached</h1>

        {/* Description */}
        <p className="text-[--clinqr-text-secondary] mb-6">
          This QR code has reached its monthly scan limit. The owner needs to upgrade their
          subscription to continue using this QR code.
        </p>

        {/* QR Code reference */}
        {shortCode && (
          <p className="text-sm text-[--clinqr-text-muted] mb-6">
            QR Code: <code className="bg-[--clinqr-bg-subtle] px-2 py-1 rounded">{shortCode}</code>
          </p>
        )}

        {/* Divider */}
        <div className="border-t border-[--clinqr-border] my-6" />

        {/* CTA for QR code owners */}
        <div className="bg-[--clinqr-primary]/10 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-[--clinqr-primary]" />
            <span className="font-semibold text-[--clinqr-text-primary]">Are you the QR code owner?</span>
          </div>
          <p className="text-sm text-[--clinqr-text-secondary] mb-4">
            Upgrade your plan to get more scans and keep your QR codes active.
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[--clinqr-primary] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Log in to upgrade <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Back to home */}
        <button
          onClick={() => navigate('/')}
          className="text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary] text-sm transition-colors"
        >
          Go to Clinqr homepage {countdown > 0 && `(${countdown}s)`}
        </button>
      </div>
    </div>
  );
}
